import { Component } from '@theme/component';
import { isClickedOutside } from '@theme/utilities';

/**
 * A custom element that displays a dropdown for variant selection with flavor images.
 *
 * @typedef {object} CustomDropdownRefs
 * @property {HTMLButtonElement} button - The button element that opens/closes the dropdown.
 * @property {HTMLDivElement} panel - The dropdown panel element.
 * @property {HTMLLIElement[]} options - The option list items.
 *
 * @extends {Component<CustomDropdownRefs>}
 */
export class CustomVariantDropdown extends Component {
  /** @type {number} */
  #selectedIndex = -1;

  /** @type {string | null} */
  #selectedOptionValueId = null;

  /** @type {HTMLLIElement | null} */
  #focusedOption = null;
  
  /** @type {boolean} */
  #isUnavailable = false;
  
  /** @type {boolean} */
  #hasImage = false;
  
  /** @type {boolean} */
  #isLoading = false;
  
  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  /** @type {HTMLElement | null} */
  #backdrop = null;

  /** @type {HTMLElement | null} */
  #originalPanelParent = null;

  /** @type {HTMLDivElement | null} */
  #shadowPanel = null;

  /** @type {HTMLDivElement | null} */
  #originalPanel = null;

  connectedCallback() {
    super.connectedCallback();

    const { button, panel, options } = this.refs;

    if (!button || !panel || !options) {
      return;
    }

    // Clean up any whitespace/text nodes in image circle from Liquid template rendering
    const buttonImage = button.querySelector('.variant-option__flavor-image-circle');
    if (buttonImage) {
      const hasImg = buttonImage.querySelector('img');
      // Remove any text nodes (whitespace) if there's no image
      if (!hasImg) {
        const textNodes = Array.from(buttonImage.childNodes).filter(node => node.nodeType === Node.TEXT_NODE);
        textNodes.forEach(node => node.remove());
      }
    }

    // Set initial selected option (this will set #hasImage and #isUnavailable correctly)
    this.#updateSelectedOption();
    
    // Set up MutationObserver to restore unavailable state when classes are removed by morphing
    // Don't call restore/ensure here as updateSelectedOption already handles initial state
    this.#setupMutationObserver();

    // Button click handler
    button.addEventListener('click', this.#handleButtonClick);
    button.addEventListener('keydown', this.#handleButtonKeyDown);

    // Panel keyboard navigation
    panel.addEventListener('keydown', this.#handlePanelKeyDown);

    // Option click handlers
    options.forEach((option, index) => {
      option.addEventListener('click', () => this.#selectOption(index));
    });

    // Listen for external updates (e.g., when variant is changed via URL or other means)
    const variantPicker = this.closest('variant-picker');
    if (variantPicker) {
      variantPicker.addEventListener('variant-update', this.#handleVariantUpdate);
    }
  }
  
  /**
   * Restores unavailable state after morphing.
   */
  #restoreUnavailableState() {
    const { button } = this.refs;
    if (!button || !this.#isUnavailable) {
      return;
    }
    
    const buttonText = button.querySelector('.variant-option__custom-dropdown-text');
    const buttonImage = button.querySelector('.variant-option__flavor-image-circle');
    
    if (buttonText && !buttonText.classList.contains('variant-option__custom-dropdown-text--unavailable')) {
      buttonText.classList.add('variant-option__custom-dropdown-text--unavailable');
    }
    
    if (buttonImage && !buttonImage.classList.contains('variant-option__flavor-image-circle--unavailable')) {
      buttonImage.classList.add('variant-option__flavor-image-circle--unavailable');
    }
  }
  
  /**
   * Ensures button image is cleared if no image should be shown.
   * Called after morphing to prevent images from being restored incorrectly.
   * Only clears if there's a mismatch - don't clear if state is already correct.
   */
  #ensureImageState() {
    const { button } = this.refs;
    if (!button) return;
    
    const buttonImage = button.querySelector('.variant-option__flavor-image-circle');
    if (!buttonImage) return;
    
    const hasImg = buttonImage.querySelector('img');
    const hasContent = buttonImage.childNodes.length > 0;
    
    // Only act if there's a mismatch between expected state and actual state
    if (!this.#hasImage) {
      // We shouldn't have an image, but one exists - clear it
      if (hasImg || hasContent) {
        // Clear all content including whitespace and any images
        while (buttonImage.firstChild) {
          buttonImage.removeChild(buttonImage.firstChild);
        }
      }
      // If already empty, do nothing (avoid unnecessary work)
    } else {
      // We should have an image - if missing, it will be restored on next updateButtonContent call
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { button, panel, options } = this.refs;

    if (button) {
      button.removeEventListener('click', this.#handleButtonClick);
      button.removeEventListener('keydown', this.#handleButtonKeyDown);
    }

    if (panel) {
      panel.removeEventListener('keydown', this.#handlePanelKeyDown);
    }

    if (options) {
      options.forEach((option) => {
        option.removeEventListener('click', this.#selectOption);
      });
    }

    document.removeEventListener('click', this.#handleClickOutside);

    const variantPicker = this.closest('variant-picker');
    if (variantPicker) {
      variantPicker.removeEventListener('variant-update', this.#handleVariantUpdate);
    }
    
    // Disconnect MutationObserver
    if (this.#mutationObserver) {
      this.#mutationObserver.disconnect();
      this.#mutationObserver = null;
    }

    // Remove backdrop if present
    if (this.#backdrop) {
      if (this.#backdrop.parentNode) {
        this.#backdrop.parentNode.removeChild(this.#backdrop);
      }
      this.#backdrop = null;
    }

    // Remove shadow panel if present
    if (this.#shadowPanel) {
      if (this.#shadowPanel.parentNode) {
        this.#shadowPanel.parentNode.removeChild(this.#shadowPanel);
      }
      this.#shadowPanel = null;
    }
  }
  
  /**
   * Sets up a MutationObserver to restore unavailable state when classes are removed by morphing.
   */
  #setupMutationObserver() {
    const { button } = this.refs;
    if (!button) return;
    
    // Disconnect existing observer if any
    if (this.#mutationObserver) {
      this.#mutationObserver.disconnect();
    }
    
    this.#mutationObserver = new MutationObserver((mutations) => {
      let shouldRestore = false;
      let buttonWasMorphed = false;
      
      for (const mutation of mutations) {
        // Check if button was morphed (attributes changed or children changed)
        if (mutation.target === button || button.contains(mutation.target)) {
          if (mutation.type === 'attributes' || mutation.type === 'childList') {
            buttonWasMorphed = true;
          }
        }
        
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target;
          const buttonText = button.querySelector('.variant-option__custom-dropdown-text');
          const buttonImage = button.querySelector('.variant-option__flavor-image-circle');
          
          // Check if unavailable classes were removed from text or image
          if ((target === buttonText && this.#isUnavailable && 
               !buttonText.classList.contains('variant-option__custom-dropdown-text--unavailable')) ||
              (target === buttonImage && this.#isUnavailable && 
               !buttonImage.classList.contains('variant-option__flavor-image-circle--unavailable'))) {
            shouldRestore = true;
            break;
          }
          
          // Also check if image was incorrectly added when it shouldn't be there
          if (target === buttonImage) {
            const hasImg = buttonImage.querySelector('img');
            // If we don't have an image according to stored state, clear any content
            if (!this.#hasImage && (hasImg || buttonImage.childNodes.length > 0)) {
              // This will trigger ensureImageState
              shouldRestore = true;
            }
          }
        }
      }
      
      // Don't clear loading state here - let updatedCallback or timeout handle it
      // The MutationObserver might fire too early during morphing
      
      if (shouldRestore) {
        // Use requestAnimationFrame to avoid infinite loops
        requestAnimationFrame(() => {
          this.#restoreUnavailableState();
          this.#ensureImageState();
        });
      }
    });
    
    // Observe changes on button and its children (including childList to detect morphing)
    this.#mutationObserver.observe(button, {
      attributes: true,
      attributeFilter: ['class', 'disabled', 'aria-busy'],
      subtree: true,
      childList: true
    });
  }

  /**
   * Checks if this dropdown is inside a quick-add modal
   * @returns {boolean}
   */
  #isInQuickAddModal() {
    return Boolean(this.closest('quick-add-dialog') || this.closest('.quick-add-modal'));
  }

  /**
   * Checks if we're on a mobile device
   * @returns {boolean}
   */
  #isMobile() {
    return window.innerWidth <= 749;
  }

  /**
   * Creates and adds backdrop for mobile bottom-sheet
   */
  #createBackdrop() {
    if (this.#backdrop) {
      return; // Already exists
    }

    const backdrop = document.createElement('div');
    backdrop.className = 'variant-dropdown-backdrop';
    
    // Add class for quick-add modal so backdrop is visible on desktop too
    const isInQuickAdd = this.#isInQuickAddModal();
    if (isInQuickAdd) {
      backdrop.classList.add('quick-add-backdrop');
      // Place backdrop inside quick-add modal but before content for proper layering
      const quickAddDialog = this.closest('quick-add-dialog');
      const dialog = quickAddDialog?.querySelector('.quick-add-modal');
      const modalContent = document.getElementById('quick-add-modal-content');
      
      if (dialog && modalContent) {
        // Insert backdrop before content so it's behind content but above modal background
        dialog.insertBefore(backdrop, modalContent);
      } else if (dialog) {
        dialog.appendChild(backdrop);
      } else {
        document.body.appendChild(backdrop);
      }
    } else {
      document.body.appendChild(backdrop);
    }
    
    backdrop.setAttribute('aria-hidden', 'true');
    backdrop.addEventListener('click', () => this.hidePanel());
    
    this.#backdrop = backdrop;
    
    // Trigger fade-in animation
    requestAnimationFrame(() => {
      backdrop.classList.add('backdrop-visible');
    });
  }

  /**
   * Removes backdrop for mobile bottom-sheet
   */
  #removeBackdrop() {
    if (!this.#backdrop) return;

    const backdrop = this.#backdrop;
    backdrop.classList.remove('backdrop-visible');
    
    // Wait for fade-out animation before removing
    const animationDuration = 300; // Match CSS animation duration
    setTimeout(() => {
      if (backdrop.parentNode) {
        backdrop.parentNode.removeChild(backdrop);
      }
      this.#backdrop = null;
    }, animationDuration);
  }

  /**
   * Creates a shadow panel that stays within the variant-picker for morphing.
   * The shadow panel is hidden and synchronized with the moved panel.
   */
  #createShadowPanel() {
    // Only create shadow panel when panel was moved (in quick-add modal)
    // Use originalPanel if it exists, otherwise use refs.panel
    const sourcePanel = this.#originalPanel || this.refs.panel;
    if (!sourcePanel || this.#shadowPanel) {
      return; // Already exists or no panel
    }

    // Clone the panel as shadow panel (deep clone to include all options)
    const shadowPanel = sourcePanel.cloneNode(true);
    shadowPanel.classList.add('variant-option__custom-dropdown-panel--shadow');
    
    // Remove ref attribute from shadow panel to prevent Component class from updating refs
    // The shadow panel should NOT be accessible via this.refs.panel
    shadowPanel.removeAttribute('ref');
    
    // Also remove ref attributes from all child elements to prevent ref conflicts
    shadowPanel.querySelectorAll('[ref]').forEach(el => el.removeAttribute('ref'));
    
    // Hide shadow panel completely (not in layout flow)
    shadowPanel.style.display = 'none';
    shadowPanel.style.visibility = 'hidden';
    shadowPanel.style.position = 'absolute';
    shadowPanel.style.left = '-9999px';
    shadowPanel.style.top = '-9999px';
    shadowPanel.setAttribute('aria-hidden', 'true');
    shadowPanel.setAttribute('hidden', '');
    
    // Add shadow panel to custom-variant-dropdown (within variant-picker structure)
    this.appendChild(shadowPanel);
    
    this.#shadowPanel = shadowPanel;
    
    // Synchronize aria-selected state with current selection (after creating shadow panel)
    // Use stored optionValueId if available, otherwise use current aria-selected state
    const shadowOptions = shadowPanel.querySelectorAll('[role="option"]');
    if (this.#selectedOptionValueId) {
      // Use stored selection
      shadowOptions.forEach((option) => {
        const matches = option.dataset.optionValueId === this.#selectedOptionValueId;
        option.setAttribute('aria-selected', matches ? 'true' : 'false');
      });
    } else {
      // Fallback: use current aria-selected state from original panel
      const { options } = this.refs;
      if (options && options.length > 0) {
        options.forEach((originalOpt, index) => {
          const isSelected = originalOpt.getAttribute('aria-selected') === 'true';
          if (shadowOptions[index]) {
            shadowOptions[index].setAttribute('aria-selected', isSelected ? 'true' : 'false');
          }
        });
      }
    }
  }

  /**
   * Synchronizes the shadow panel's aria-selected state with the selected option.
   * @param {string | null} selectedOptionValueId - The selected option value ID
   */
  #syncShadowPanel(selectedOptionValueId) {
    if (!this.#shadowPanel || !selectedOptionValueId) {
      return;
    }

    // Find all options in shadow panel
    const shadowOptions = this.#shadowPanel.querySelectorAll('[role="option"]');
    
    // Update aria-selected state in shadow panel
    shadowOptions.forEach((option) => {
      const matches = option.dataset.optionValueId === selectedOptionValueId;
      option.setAttribute('aria-selected', matches ? 'true' : 'false');
    });
  }

  /**
   * Gets whether the panel is hidden.
   * @returns {boolean}
   */
  get isHidden() {
    // Only use originalPanel in quick-add modal (when panel was moved)
    // On product page, always use refs.panel
    const panel = this.#isInQuickAddModal() && this.#originalPanel ? this.#originalPanel : this.refs.panel;
    const hidden = !panel || panel.hasAttribute('hidden');
    return hidden;
  }

  /**
   * Handles button click to toggle dropdown.
   */
  #handleButtonClick = () => {
    const { button } = this.refs;
    
    // Don't allow opening dropdown while loading or if button is disabled
    if (this.#isLoading || (button && button.disabled)) {
      return;
    }
    
    if (this.isHidden) {
      this.showPanel();
    } else {
      this.hidePanel();
    }
  };

  /**
   * Handles button keyboard events.
   * @param {KeyboardEvent} event
   */
  #handleButtonKeyDown = (event) => {
    const { button } = this.refs;
    // Don't allow keyboard interaction while loading or if button is disabled
    if (this.#isLoading || (button && button.disabled)) {
      return;
    }
    
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this.isHidden) {
          this.showPanel();
        } else {
          this.hidePanel();
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (this.isHidden) {
          this.showPanel();
        }
        this.focusFirstOption();
        break;
      case 'Escape':
        if (!this.isHidden) {
          event.preventDefault();
          this.hidePanel();
        }
        break;
    }
  };

  /**
   * Handles panel keyboard navigation.
   * @param {KeyboardEvent} event
   */
  #handlePanelKeyDown = (event) => {
    const { options } = this.refs;
    if (!options || options.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#focusNextOption();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#focusPreviousOption();
        break;
      case 'Home':
        event.preventDefault();
        this.#focusFirstOption();
        break;
      case 'End':
        event.preventDefault();
        this.#focusLastOption();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this.#focusedOption) {
          const index = options.indexOf(this.#focusedOption);
          if (index !== -1) {
            this.#selectOption(index);
          }
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.hidePanel();
        this.refs.button?.focus();
        break;
      case 'Tab':
        this.hidePanel();
        break;
    }
  };

  /**
   * Handles clicks outside the dropdown.
   * @param {PointerEvent} event
   */
  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.hidePanel();
    }
  };

  /**
   * Shows the dropdown panel.
   */
  showPanel() {
    const { button } = this.refs;
    const isInQuickAdd = this.#isInQuickAddModal();
    
    // Get the actual panel - prioritize originalPanel if it exists (when panel was moved)
    // Otherwise use refs.panel, or fallback to originalPanel if refs.panel is null
    const actualPanel = this.#originalPanel || this.refs.panel;
    
    // Don't allow opening panel while loading or if button is disabled
    if (!actualPanel || !button) {
      return;
    }
    
    if (this.#isLoading || button.disabled) {
      return;
    }
    
    if (!this.isHidden) {
      return;
    }

    document.addEventListener('click', this.handleClickOutside);

    // Check if we're on mobile
    const isMobile = this.#isMobile();
    
    if (isInQuickAdd) {
      // Quick-add modal: Always use bottom-sheet behavior (mobile and desktop)
      // Add scroll lock to prevent body scrolling
      document.documentElement.setAttribute('scroll-lock', '');
      document.body.style.overflow = 'hidden';
      
      // Create and add backdrop
      this.#createBackdrop();
      
      const modalContent = document.getElementById('quick-add-modal-content');
      const quickAddDialog = this.closest('quick-add-dialog');
      const dialog = quickAddDialog?.querySelector('.quick-add-modal');
      
      if (modalContent) {
        modalContent.classList.add('dropdown-open');
      }
      
      // Allow overflow for dropdown panel
      if (dialog) {
        dialog.classList.add('dropdown-open');
      }
      
      // Remove hidden attribute BEFORE moving panel so it's visible
      actualPanel.removeAttribute('hidden');
      
      // Move panel outside modal content to dialog level so it can appear above backdrop
      if (dialog && actualPanel.parentElement !== dialog) {
        // Store original parent and panel reference to restore later
        this.#originalPanelParent = actualPanel.parentElement;
        this.#originalPanel = actualPanel; // Store reference to original panel
        dialog.appendChild(actualPanel);
        
        // Create shadow panel within variant-picker for morphing
        this.#createShadowPanel();
        // Synchronize shadow panel with current selection
        if (this.#selectedOptionValueId) {
          this.#syncShadowPanel(this.#selectedOptionValueId);
        }
      }
      
      // Set panel width to full width (not 100vw to avoid scrollbar issues)
      actualPanel.style.width = '100%';
      actualPanel.style.left = '0';
      actualPanel.style.right = '0';
      
      // Fixed positioning at bottom
      actualPanel.style.position = 'fixed';
      actualPanel.style.bottom = '0';
      actualPanel.style.top = 'auto';
      actualPanel.style.maxHeight = '60vh';
      actualPanel.style.zIndex = '9999';
      actualPanel.style.transform = '';
    } else if (isMobile) {
      // Mobile: Use fixed bottom positioning (bottom-sheet overlay)
      // Add scroll lock to prevent body scrolling
      document.documentElement.setAttribute('scroll-lock', '');
      document.body.style.overflow = 'hidden';
      
      // Create and add backdrop
      this.#createBackdrop();
      
      // Get panel for mobile (not in quick-add)
      const mobilePanel = this.refs.panel;
      if (!mobilePanel) {
        return;
      }
      
      // Set panel width to full width (not 100vw to avoid scrollbar issues)
      mobilePanel.style.width = '100%';
      mobilePanel.style.left = '0';
      mobilePanel.style.right = '0';
      
      // Fixed positioning at bottom
      mobilePanel.style.position = 'fixed';
      mobilePanel.style.bottom = '0';
      mobilePanel.style.top = 'auto';
      mobilePanel.style.maxHeight = '60vh';
      mobilePanel.style.zIndex = '9999';
      mobilePanel.style.transform = '';
      
      // Remove closing class if present
      mobilePanel.classList.remove('bottom-sheet-closing');
      
      // Remove hidden attribute
      if (mobilePanel.hasAttribute('hidden')) {
        mobilePanel.removeAttribute('hidden');
      }
    } else {
      // Desktop: Normal behavior with absolute positioning
      const desktopPanel = this.refs.panel;
      if (!desktopPanel) {
        return;
      }
      
      const buttonRect = button.getBoundingClientRect();
      const wrapper = this.closest('.variant-option__custom-dropdown-wrapper');
      if (wrapper) {
        const wrapperRect = wrapper.getBoundingClientRect();
        desktopPanel.style.width = `${wrapperRect.width}px`;
        desktopPanel.style.left = '0';
      } else {
        desktopPanel.style.width = `${buttonRect.width}px`;
      }
      
      // Reset positioning styles
      desktopPanel.style.position = '';
      desktopPanel.style.bottom = '';
      desktopPanel.style.top = '';
      desktopPanel.style.right = '';
      desktopPanel.style.zIndex = '';
      desktopPanel.style.maxHeight = '';
      desktopPanel.style.transform = '';
      
      // Remove closing class if present
      desktopPanel.classList.remove('bottom-sheet-closing');
      
      // Remove hidden attribute
      if (desktopPanel.hasAttribute('hidden')) {
        desktopPanel.removeAttribute('hidden');
      }
    }
    // Set aria-expanded on button
    button.setAttribute('aria-expanded', 'true');

    // Focus first option or selected option
    // Always use refs.options for focusing (they are the interactive options)
    if (this.#selectedIndex >= 0 && this.refs.options?.[this.#selectedIndex]) {
      this.#focusOption(this.#selectedIndex);
    } else {
      this.#focusFirstOption();
    }
  }

  /**
   * Hides the dropdown panel.
   * Note: Shadow panel is NOT removed here - it stays for morphing purposes.
   * Shadow panel is only removed in disconnectedCallback() or updatedCallback() after morphing.
   */
  hidePanel() {
    const { button } = this.refs;
    const isInQuickAdd = this.#isInQuickAddModal();
    
    // Get the actual panel - prioritize originalPanel if it exists (when panel was moved)
    // Otherwise use refs.panel, or fallback to originalPanel if refs.panel is null
    const actualPanel = this.#originalPanel || this.refs.panel;
    
    if (!actualPanel || !button) {
      return;
    }
    
    // Check if panel is hidden
    const isHidden = !actualPanel || actualPanel.hasAttribute('hidden');
    
    if (isHidden) {
      return;
    }

    document.removeEventListener('click', this.#handleClickOutside);

    const isMobile = this.#isMobile();

    // Handle quick-add modal (always uses bottom-sheet behavior)
    if (isInQuickAdd) {
      // Remove backdrop first (with fade-out)
      this.#removeBackdrop();
      
      // Add closing animation class for bottom-sheet
      actualPanel.classList.add('bottom-sheet-closing');
      
      // Immediately hide panel and reset styles to prevent it from staying visible
      // Hide panel FIRST to prevent CSS from keeping it visible
      actualPanel.setAttribute('hidden', '');
      
      // Also set display: none explicitly with !important to force hiding (override CSS)
      actualPanel.style.setProperty('display', 'none', 'important');
      
      // Remove shadow panel FIRST before restoring panel
      // This ensures refs.panel points to the correct panel when Component class updates refs
      if (this.#shadowPanel && this.#shadowPanel.parentNode) {
        this.#shadowPanel.parentNode.removeChild(this.#shadowPanel);
        this.#shadowPanel = null;
      }
      
      // Reset positioning styles immediately to clear inline styles that keep panel visible
      // Also reset display style to remove !important
      actualPanel.style.position = '';
      actualPanel.style.bottom = '';
      actualPanel.style.top = '';
      actualPanel.style.left = '';
      actualPanel.style.right = '';
      actualPanel.style.width = '';
      actualPanel.style.maxHeight = '';
      actualPanel.style.transform = '';
      actualPanel.style.zIndex = '';
      actualPanel.style.removeProperty('display'); // Remove display: none !important
      
      // Restore panel to original parent immediately
      if (this.#originalPanelParent && actualPanel.parentElement !== this.#originalPanelParent) {
        this.#originalPanelParent.appendChild(actualPanel);
        this.#originalPanelParent = null;
        
        // Wait for Component class to update refs before clearing originalPanel reference
        // This ensures refs.panel points to the restored panel
        requestAnimationFrame(() => {
          // Check if refs.panel is now correct
          if (this.refs.panel && this.refs.panel === actualPanel) {
            this.#originalPanel = null;
          } else {
            // Keep originalPanel reference until refs are updated
            // Try again after a short delay
            setTimeout(() => {
              if (this.refs.panel && this.refs.panel === actualPanel) {
                this.#originalPanel = null;
              }
              // Keep originalPanel reference as fallback if refs still not updated
            }, 100);
          }
        });
      } else {
        // Only clear if refs.panel exists
        if (this.refs.panel) {
          this.#originalPanel = null;
        }
      }
      
      button.setAttribute('aria-expanded', 'false');
      this.#focusedOption = null;
      
      // Wait for animation to complete before cleaning up
      const animationDuration = 300; // Match CSS animation duration
      setTimeout(() => {
        // Remove scroll lock
        document.documentElement.removeAttribute('scroll-lock');
        document.body.style.overflow = '';
        
        const modalContent = document.getElementById('quick-add-modal-content');
        const quickAddDialog = this.closest('quick-add-dialog');
        const dialog = quickAddDialog?.querySelector('.quick-add-modal');
        
        if (modalContent) {
          modalContent.classList.remove('dropdown-open');
        }
        
        if (dialog) {
          dialog.classList.remove('dropdown-open');
        }
        
        // Remove closing class after animation
        // Use originalPanel if it exists (when panel was moved), otherwise use refs.panel
        const finalPanel = this.#originalPanel || this.refs.panel;
        if (finalPanel) {
          finalPanel.classList.remove('bottom-sheet-closing');
        }
      }, animationDuration);
      
      return; // Early return to prevent immediate hiding
    } else if (isMobile) {
      // Mobile: Remove backdrop first (with fade-out)
      this.#removeBackdrop();
      
      // Add closing animation class for mobile bottom-sheet
      actualPanel.classList.add('bottom-sheet-closing');
      
      // Wait for animation to complete before hiding
      const animationDuration = 300; // Match CSS animation duration
      setTimeout(() => {
        // Remove scroll lock for mobile bottom-sheet
        document.documentElement.removeAttribute('scroll-lock');
        document.body.style.overflow = '';
        
        // Reset positioning styles for mobile
        actualPanel.style.position = '';
        actualPanel.style.bottom = '';
        actualPanel.style.top = '';
        actualPanel.style.left = '';
        actualPanel.style.right = '';
        actualPanel.style.width = '';
        actualPanel.style.maxHeight = '';
        actualPanel.style.transform = '';
        actualPanel.style.zIndex = '';
        
        // Remove closing class
        actualPanel.classList.remove('bottom-sheet-closing');
        
        // Hide panel after animation
        actualPanel.setAttribute('hidden', '');
        button.setAttribute('aria-expanded', 'false');
        this.#focusedOption = null;
      }, animationDuration);
      
      return; // Early return to prevent immediate hiding
    }

    // Desktop: Normal behavior - just hide the panel
    actualPanel.setAttribute('hidden', '');
    button.setAttribute('aria-expanded', 'false');
    this.#focusedOption = null;
  }

  /**
   * Selects an option by index.
   * @param {number} index
   */
  #selectOption(index) {
    // Get options from the actual panel being used
    // Use originalPanel if it exists (when panel was moved), otherwise use refs.options
    const isInQuickAdd = this.#isInQuickAddModal();
    
    let options = null;
    
    if (isInQuickAdd && this.#originalPanel) {
      // Panel is moved, get options from the moved panel
      const optionElements = this.#originalPanel.querySelectorAll('[role="option"]');
      options = Array.from(optionElements);
    } else {
      // Normal case: use refs.options
      options = this.refs.options;
    }
    
    if (!options || !Array.isArray(options) || options.length === 0 || index < 0 || index >= options.length) {
      return;
    }

    const option = options[index];

    // Update selected state in all options
    options.forEach((opt, i) => {
      const isSelected = i === index;
      opt.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });
    
    // Also update refs.options if they exist and are different from the options we're using
    // This ensures consistency when the panel is moved (refs.options might point to shadow panel or different elements)
    if (this.refs.options && Array.isArray(this.refs.options) && this.refs.options.length > 0) {
      // Check if refs.options are different elements
      const refsOptionsAreDifferent = this.refs.options[0] !== options[0];
      if (refsOptionsAreDifferent) {
        this.refs.options.forEach((refOpt, i) => {
          const isSelected = i === index;
          refOpt.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        });
      }
    }

    this.#selectedIndex = index;
    // Store the selected option value ID so we can restore it after morphing
    this.#selectedOptionValueId = option.dataset.optionValueId || null;
    
    // Synchronize shadow panel with selected option (if it exists)
    // This ensures morphing preserves the selection
    if (this.#selectedOptionValueId) {
      this.#syncShadowPanel(this.#selectedOptionValueId);
    }
    
    this.#updateButtonContent(option);
    
    this.hidePanel();

    // Set loading state to prevent dropdown from being opened during variant update
    this.#setLoadingState();
    
    // Clear loading state after morphing completes
    // Use a longer timeout to ensure variant update is fully complete
    // The updatedCallback will also try to clear it, but this is a fallback
    setTimeout(() => {
      this.#clearLoadingState();
    }, 500);

    // Find the variant-picker component to dispatch the event
    // Try multiple ways to find it since panel might be moved to dialog
    let variantPicker = this.closest('variant-picker');
    if (!variantPicker) {
      // If panel was moved, try finding variant-picker from original parent
      const actualPanel = this.#originalPanel || this.refs.panel;
      const originalParent = this.#originalPanelParent || actualPanel?.parentElement;
      if (originalParent) {
        variantPicker = originalParent.closest('variant-picker');
      }
    }
    // Also try finding it from the quick-add modal
    if (!variantPicker) {
      const quickAddDialog = this.closest('quick-add-dialog');
      if (quickAddDialog) {
        variantPicker = quickAddDialog.querySelector('variant-picker');
      }
    }
    
    // Create a synthetic change event that mimics a select element change
    // This allows variant-picker.js to handle it the same way as a standard select
    const changeEvent = new Event('change', { bubbles: true, cancelable: true });
    
    // Set the target to the option element so variant-picker can find it
    Object.defineProperty(changeEvent, 'target', {
      value: option,
      writable: false,
    });
    
    // Dispatch directly on variant-picker (like it works on product page)
    // This ensures the event is handled correctly even when panel is moved
    if (variantPicker) {
      // Create event with option as target so variant-picker can find it
      const variantPickerEvent = new Event('change', { bubbles: true, cancelable: true });
      Object.defineProperty(variantPickerEvent, 'target', {
        value: option,
        writable: false,
      });
      variantPicker.dispatchEvent(variantPickerEvent);
    } else {
      console.warn('[CustomVariantDropdown] variant-picker not found! Event may not be handled.');
      // Fallback: dispatch from component
      this.dispatchEvent(changeEvent);
    }
  }

  /**
   * Updates the button content to show selected option.
   * @param {HTMLLIElement} option
   */
  #updateButtonContent(option) {
    const { button } = this.refs;
    if (!button || !option) {
      return;
    }

    const imageElement = option.querySelector('.variant-option__flavor-image-circle');
    const textElement = option.querySelector('.variant-option__custom-dropdown-option-text');
    const soldOutLabel = option.querySelector('.variant-option__sold-out-label');
    
    // Check multiple ways to determine if unavailable
    const datasetAvailable = option.dataset?.optionAvailable;
    const hasUnavailableClass = option.classList?.contains('variant-option__custom-dropdown-option--unavailable');
    const hasSoldOutLabel = soldOutLabel !== null;
    const isUnavailable = datasetAvailable === 'false' || hasUnavailableClass || hasSoldOutLabel;
    
    // Store unavailable state in component instance so it survives morphing
    this.#isUnavailable = isUnavailable;

    if (!textElement) {
      return;
    }

    // Update button text (within content wrapper if it exists)
    const buttonContent = button.querySelector('.variant-option__custom-dropdown-content');
    const buttonText = buttonContent 
      ? buttonContent.querySelector('.variant-option__custom-dropdown-text')
      : button.querySelector('.variant-option__custom-dropdown-text');
    
    if (buttonText) {
      // Update text content
      buttonText.textContent = textElement.textContent.trim();
      
      // Update text unavailable state
      // Remove first to ensure clean state
      buttonText.classList.remove('variant-option__custom-dropdown-text--unavailable');
      if (isUnavailable) {
        buttonText.classList.add('variant-option__custom-dropdown-text--unavailable');
      }
    }

    // Update button image
    const buttonImage = button.querySelector('.variant-option__flavor-image-circle');
    if (buttonImage) {
      // Check if source option has an actual image (not just empty circle)
      const img = imageElement?.querySelector('img');
      const hasImage = img && img.src;
      
      // Store image state so we can restore it after morphing
      this.#hasImage = !!hasImage;
      
      if (hasImage) {
        // Source has image, copy it
        let buttonImg = buttonImage.querySelector('img');
        if (buttonImg) {
          // Update existing img
          buttonImg.src = img.src;
          buttonImg.alt = img.alt;
          if (img.srcset) buttonImg.srcset = img.srcset;
        } else {
          // Create new img
          buttonImg = document.createElement('img');
          buttonImg.src = img.src;
          buttonImg.alt = img.alt;
          buttonImg.width = img.width;
          buttonImg.height = img.height;
          buttonImg.loading = img.loading;
          if (img.srcset) buttonImg.srcset = img.srcset;
          buttonImage.innerHTML = '';
          buttonImage.appendChild(buttonImg);
        }
      } else {
        // No image in source option, clear button image completely
        // Only clear if there's actually content to remove (avoid unnecessary DOM manipulation)
        if (buttonImage.childNodes.length > 0) {
          // Remove all children including any whitespace/text nodes
          while (buttonImage.firstChild) {
            buttonImage.removeChild(buttonImage.firstChild);
          }
        }
      }

      // Always update image unavailable state AFTER copying content
      // Remove first to ensure clean state
      buttonImage.classList.remove('variant-option__flavor-image-circle--unavailable');
      if (isUnavailable && hasImage) {
        buttonImage.classList.add('variant-option__flavor-image-circle--unavailable');
      }
    }

    // Update sold out label
    let buttonSoldOutLabel = button.querySelector('.variant-option__sold-out-label');
    
    if (isUnavailable && soldOutLabel) {
      if (!buttonSoldOutLabel) {
        buttonSoldOutLabel = soldOutLabel.cloneNode(true);
        button.appendChild(buttonSoldOutLabel);
      } else {
        buttonSoldOutLabel.textContent = soldOutLabel.textContent;
      }
    } else if (buttonSoldOutLabel) {
      buttonSoldOutLabel.remove();
    }
    // Note: Count text stays the same, don't update it
  }
  
  /**
   * Called after the component is updated by morphing.
   * Restores unavailable state that may have been lost during morphing.
   */
  updatedCallback() {
    super.updatedCallback?.();
    
    // Clean up shadow panel after morphing (new morphed version will have its own shadow if needed)
    if (this.#shadowPanel) {
      if (this.#shadowPanel.parentNode) {
        this.#shadowPanel.parentNode.removeChild(this.#shadowPanel);
      }
      this.#shadowPanel = null;
    }
    
    // Don't call updateSelectedOption here - variant-picker.updateSelectedOption already handles it
    // Calling it here causes the selection to reset to the first variant after morphing
    
    // Restore unavailable state after morphing with multiple attempts to ensure it sticks
    // Only restore if we have stored state (avoid running on initial load before state is set)
    if (this.#isUnavailable !== undefined || this.#hasImage !== undefined) {
      this.#restoreUnavailableState();
      this.#ensureImageState();
      requestAnimationFrame(() => {
        this.#restoreUnavailableState();
        this.#ensureImageState();
      });
      setTimeout(() => {
        this.#restoreUnavailableState();
        this.#ensureImageState();
      }, 0);
      setTimeout(() => {
        this.#restoreUnavailableState();
        this.#ensureImageState();
      }, 10);
    }
    
    // Clear loading state after morphing completes
    // Use a delay to ensure morphing is fully complete
    if (this.#isLoading) {
      setTimeout(() => {
        this.#clearLoadingState();
      }, 200);
    }
  }
  
  /**
   * Clears the loading state and re-enables the button.
   */
  #clearLoadingState() {
    if (this.#isLoading) {
      this.#isLoading = false;
      const { button } = this.refs;
      if (button) {
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }
    }
  }
  
  /**
   * Sets the loading state and disables the button.
   */
  #setLoadingState() {
    this.#isLoading = true;
    const { button } = this.refs;
    if (button) {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    }
  }

  /**
   * Focuses an option by index.
   * @param {number} index
   */
  #focusOption(index) {
    const { options } = this.refs;
    if (!options || index < 0 || index >= options.length) return;

    // Remove focus from previous option
    if (this.#focusedOption) {
      this.#focusedOption.classList.remove('variant-option__custom-dropdown-option--focused');
    }

    // Focus new option
    const option = options[index];
    option.classList.add('variant-option__custom-dropdown-option--focused');
    option.focus();
    this.#focusedOption = option;

    // Scroll into view if needed
    option.scrollIntoView({ block: 'nearest' });
  }

  /**
   * Focuses the first option.
   */
  #focusFirstOption() {
    const { options } = this.refs;
    if (!options || options.length === 0) return;
    this.#focusOption(0);
  }

  /**
   * Focuses the last option.
   */
  #focusLastOption() {
    const { options } = this.refs;
    if (!options || options.length === 0) return;
    this.#focusOption(options.length - 1);
  }

  /**
   * Focuses the next option.
   */
  #focusNextOption() {
    const { options } = this.refs;
    if (!options || options.length === 0) return;

    let nextIndex = this.#selectedIndex + 1;
    if (this.#focusedOption) {
      const currentIndex = options.indexOf(this.#focusedOption);
      if (currentIndex !== -1) {
        nextIndex = currentIndex + 1;
      }
    }

    if (nextIndex >= options.length) {
      nextIndex = 0;
    }

    this.#focusOption(nextIndex);
  }

  /**
   * Focuses the previous option.
   */
  #focusPreviousOption() {
    const { options } = this.refs;
    if (!options || options.length === 0) return;

    let prevIndex = this.#selectedIndex - 1;
    if (this.#focusedOption) {
      const currentIndex = options.indexOf(this.#focusedOption);
      if (currentIndex !== -1) {
        prevIndex = currentIndex - 1;
      }
    }

    if (prevIndex < 0) {
      prevIndex = options.length - 1;
    }

    this.#focusOption(prevIndex);
  }

  /**
   * Updates the selected option based on aria-selected attribute.
   */
  #updateSelectedOption() {
    const { options } = this.refs;
    if (!options || options.length === 0) return;
    
    // First, try to find option by stored option value ID (most reliable after morphing)
    if (this.#selectedOptionValueId) {
      const optionByValueId = options.find((opt) => opt.dataset.optionValueId === this.#selectedOptionValueId);
      if (optionByValueId) {
        const foundIndex = options.indexOf(optionByValueId);
        
        // Update aria-selected to match stored state
        options.forEach((opt, i) => {
          opt.setAttribute('aria-selected', i === foundIndex ? 'true' : 'false');
        });
        
        this.#selectedIndex = foundIndex;
        
        // Update button content with found option
        try {
          this.#updateButtonContent(optionByValueId);
          return;
        } catch (error) {
          console.error('Error updating button content with option by value ID:', error);
        }
      } else {
        console.warn('[CustomVariantDropdown] Option with value ID not found:', this.#selectedOptionValueId);
      }
    }
    
    // Second, check if we have a stored selected index (from previous selection)
    // This is important when panel is moved and DOM is morphed - prioritize stored state
    if (this.#selectedIndex >= 0 && this.#selectedIndex < options.length) {
      const storedOption = options[this.#selectedIndex];
      
      // Update aria-selected to match stored state
      options.forEach((opt, i) => {
        opt.setAttribute('aria-selected', i === this.#selectedIndex ? 'true' : 'false');
      });
      
      // Store the option value ID for future reference
      this.#selectedOptionValueId = storedOption.dataset.optionValueId || null;
      
      // Update button content with stored option
      try {
        this.#updateButtonContent(storedOption);
        return;
      } catch (error) {
        console.error('Error updating button content with stored option:', error);
      }
    }
    
    // Fallback: try to find selected option by aria-selected attribute
    const selectedOption = options.find((option) => option.getAttribute('aria-selected') === 'true');
    
    if (selectedOption) {
      this.#selectedIndex = options.indexOf(selectedOption);
      this.#selectedOptionValueId = selectedOption.dataset.optionValueId || null;
      
      // Only update button content if we have a valid option
      // #updateButtonContent will set #hasImage and #isUnavailable correctly
      try {
        this.#updateButtonContent(selectedOption);
        // Don't call ensureImageState here - updateButtonContent already handles it correctly
      } catch (error) {
        console.error('Error updating button content:', error);
      }
    } else {
      console.warn('[CustomVariantDropdown] updateSelectedOption - No selected option found, resetting to first option');
      // If nothing is selected, select the first available option
      if (options.length > 0) {
        this.#selectedIndex = 0;
        this.#selectedOptionValueId = options[0].dataset.optionValueId || null;
        options[0].setAttribute('aria-selected', 'true');
        this.#updateButtonContent(options[0]);
      }
    }
  }

  /**
   * Handles variant update events from variant-picker.
   * @param {Event} event
   */
  #handleVariantUpdate = () => {
    // Don't update selected option here - let variant-picker handle it via updateSelectedOption
    // This prevents resetting the selection after user selects a variant
    // Only restore unavailable/image state if needed
    if (this.#isUnavailable !== undefined || this.#hasImage !== undefined) {
      requestAnimationFrame(() => {
        this.#restoreUnavailableState();
        this.#ensureImageState();
      });
    }
  };
}

if (!customElements.get('custom-variant-dropdown')) {
  customElements.define('custom-variant-dropdown', CustomVariantDropdown);
}
